const EXPIRE_DATE = "Thu, 01-Jan-1970 00:00:01 GMT";

/**
 * Returns the domain scopes to try when expiring a cookie: host-only (no Domain attribute),
 * then every parent domain with and without a leading dot, excluding the bare TLD.
 */
const getDomainScopes = (hostname: string): (string | null)[] => {
	const parts = hostname.split(".");
	const scopes: (string | null)[] = [null];

	for (let index = 0; index < parts.length - 1; index++) {
		const domain = parts.slice(index).join(".");
		scopes.push(domain, `.${domain}`);
	}

	return scopes;
};

/**
 * Returns "/" plus every ancestor of the current path.
 */
const getPathScopes = (pathname: string): string[] => {
	const paths = ["/"];
	let current = "";

	pathname
		.split("/")
		.filter(Boolean)
		.forEach(segment => {
			current += `/${segment}`;
			paths.push(current);
		});

	return paths;
};

/**
 * Expires a single cookie by name across every domain and path scope.
 */
const expireCookie = (name: string, domains: (string | null)[], paths: string[]): void => {
	paths.forEach(path => {
		domains.forEach(domain => {
			const domainAttribute = domain ? `; domain=${domain}` : "";
			document.cookie = `${name}=; expires=${EXPIRE_DATE}; path=${path}${domainAttribute}`;
		});
	});
};

/**
 * Expires every cookie across all parent domains (including host-only) and path segments.
 * Only JS-accessible cookies are reachable: HttpOnly cookies (typically auth/session) are
 * invisible to `document.cookie` and are not cleared here.
 */
const clearCookies = (): void => {
	const domains = getDomainScopes(window.location.hostname);
	const paths = getPathScopes(window.location.pathname);

	document.cookie.split("; ").forEach(cookie => {
		const name = cookie.split("=")[0].trim();

		if (name) {
			expireCookie(name, domains, paths);
		}
	});
};

/**
 * Clears cookies, localStorage and sessionStorage for the current origin (best-effort).
 */
export const clearBrowserStorage = () => {
	try {
		clearCookies();
		localStorage.clear();
		sessionStorage.clear();
	} catch {
		// best-effort: storage may be unavailable in the current context
	}
};
